import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Sparkles, Mic, Upload, ClipboardPaste, Loader2, X, Inbox, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, hasFeature } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { subjectsOf } from "@/lib/constants";
import { CourseCard } from "@/components/app/CourseCard";

const TITLES = { enregistrements: "Mes enregistrements", fiches: "Mes fiches de révision", quiz: "Mes quiz", favoris: "Mes cours favoris" };

const QuickActions = () => (
  <div className="grid gap-3 sm:grid-cols-3">
    {[
      ["/app/enregistrer", Mic, "Enregistrer", "Avec le micro, en direct", "bg-brand text-white border-transparent"],
      ["/app/importer", Upload, "Importer un audio", "MP3, WAV ou M4A", ""],
      ["/app/texte", ClipboardPaste, "Coller des notes", "Pour une fiche propre", ""],
    ].map(([to, Icon, t, d, cls]) => (
      <Link key={to} to={to} data-testid={`quick-action-${to.split("/").pop()}`} className={`card-flat card-hover flex items-center gap-4 p-5 ${cls}`}>
        <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${cls ? "bg-white/15" : "bg-accent text-accent-foreground"}`}><Icon className="h-5 w-5" /></span>
        <span><span className="block font-bold">{t}</span><span className={`text-sm ${cls ? "text-white/80" : "text-muted-foreground"}`}>{d}</span></span>
      </Link>
    ))}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const vue = params.get("vue"), matiere = params.get("matiere");
  const [q, setQ] = useState("");
  const [smart, setSmart] = useState(false);
  const [courses, setCourses] = useState(null);
  const [smartResults, setSmartResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const load = useCallback(async (query = "") => {
    try {
      const { data } = await api.get("/courses", { params: { q: query || undefined, subject: matiere || undefined, view: vue || undefined } });
      setCourses(data);
    } catch (e) {
      toast.error(errMsg(e));
      setCourses([]);
    }
  }, [vue, matiere]);

  useEffect(() => { setSmartResults(null); load(q); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!courses?.some((c) => c.status === "processing")) return;
    const t = setTimeout(() => load(q), 5000);
    return () => clearTimeout(t);
  }, [courses, load, q]);

  const onSearch = async (e) => {
    e.preventDefault();
    if (smart && q.trim().length >= 2) {
      setSearching(true);
      try {
        const { data } = await api.post("/search/smart", { query: q });
        setSmartResults(data);
      } catch (err) {
        toast.error(errMsg(err));
      } finally {
        setSearching(false);
      }
    } else {
      setSmartResults(null);
      load(q);
    }
  };

  const toggleFav = async (c) => {
    try {
      await api.patch(`/courses/${c.course_id}`, { favorite: !c.favorite });
      setCourses((cs) => (vue === "favoris" && c.favorite ? cs.filter((x) => x.course_id !== c.course_id) : cs.map((x) => (x.course_id === c.course_id ? { ...x, favorite: !c.favorite } : x))));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const list = smartResults ?? courses;
  const canSmart = hasFeature(user, "smart_search");
  const title = matiere || TITLES[vue] || "Mes cours";

  return (
    <div data-testid="dashboard-page">
      <p className="eyebrow">Bonjour {user?.name?.split(" ")[0]}</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="dashboard-title">{title}</h1>
      {!vue && !matiere && <div className="mt-8"><QuickActions /></div>}

      <form onSubmit={onSearch} className="mt-8 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="course-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={smart ? "Ex. : le cours où on parlait de la photosynthèse" : "Rechercher une notion, un mot-clé, un titre…"}
            className="h-12 w-full rounded-full border bg-card pl-11 pr-10 text-sm outline-none transition-[border-color,box-shadow] focus:border-primary/50 focus:ring-4 focus:ring-primary/10" />
          {q && <button type="button" data-testid="course-search-clear" onClick={() => { setQ(""); setSmartResults(null); load(""); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
        </div>
        <button type="button" data-testid="smart-search-toggle"
          onClick={() => (canSmart ? setSmart(!smart) : toast.info("La recherche intelligente est incluse dans l'offre Pro."))}
          className={`inline-flex h-12 items-center justify-center gap-2 rounded-full border px-5 text-sm font-semibold transition-colors ${smart ? "border-primary bg-accent text-accent-foreground" : "bg-card hover:border-primary/40"}`}>
          {canSmart ? <Sparkles className="h-4 w-4" /> : <Crown className="h-4 w-4" />} Recherche intelligente
        </button>
        <button data-testid="course-search-submit" className="h-12 rounded-full bg-foreground px-6 text-sm font-semibold text-background transition-opacity hover:opacity-85">
          {searching ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Rechercher"}
        </button>
      </form>

      <div className="mt-5 flex flex-wrap gap-2">
        <button data-testid="subject-filter-all" onClick={() => { params.delete("matiere"); setParams(params); }}
          className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${!matiere ? "border-foreground bg-foreground text-background" : "hover:border-primary/40"}`}>Toutes</button>
        {subjectsOf(user).map((s) => (
          <button key={s} data-testid={`subject-filter-${s}`} onClick={() => { params.set("matiere", s); setParams(params); }}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${matiere === s ? "border-foreground bg-foreground text-background" : "hover:border-primary/40"}`}>{s}</button>
        ))}
      </div>

      <div className="mt-8">
        {list === null ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="card-flat h-48 animate-pulse bg-muted/50" />)}</div>
        ) : list.length === 0 ? (
          <div className="card-flat flex flex-col items-center px-6 py-16 text-center" data-testid="courses-empty-state">
            <Inbox className="h-10 w-10 text-muted-foreground" />
            <p className="mt-4 font-semibold">{q ? "Aucun résultat pour cette recherche." : "Aucun cours ici pour l'instant."}</p>
            <p className="mt-1 text-sm text-muted-foreground">Enregistre ton premier cours, importe un audio ou colle tes notes.</p>
            <Link to="/app/enregistrer" data-testid="empty-record-cta" className="bg-brand mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white"><Mic className="h-4 w-4" /> Enregistrer un cours</Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="courses-grid">
            {list.map((c, i) => <CourseCard key={c.course_id} c={c} i={i} onToggleFav={toggleFav} reason={c.reason} />)}
          </div>
        )}
      </div>
    </div>
  );
}
